// src/oura/onboarding/oneSetup.ts
//
// One setup: the questions about their business and the access we need, on
// one page the merchant works down, instead of a Google Form in one email and
// an access request in another.
//
// What the merchant's Setup page shows for each step, worked out here with no
// React, so each rule can be read and checked on its own.

import type { AccessItem, MerchantForm, MerchantOnboarding, SetupAnswers, SetupStepKey } from "@service/aeo/onboarding";
import { withDefaults } from "./setupQuestions";
import { isOuraProd } from "../role";
import { callName, isHeld } from "./derive";

/**
 * The guided Setup page. Off in production unless exactly "true", like every
 * Oura flag; on everywhere else, where it runs on the same live routes the
 * old list did. With it off the Setup page is the list it was.
 */
export const ONE_SETUP_UI: boolean = import.meta.env.VITE_ENABLE_OURA_ONE_SETUP === "true" || !isOuraProd();

/** The steps on the page. `google` holds Search Console and Analytics: one Google sign-in gives both. */
export type MerchantStepKey = "form" | "gbp" | "website" | "google";

/**
 * done:    finished.
 * waiting: their part is done; we are checking the invite.
 * with:    sent to someone else, not done yet.
 * open:    theirs to do.
 */
export type MerchantStepState = "done" | "waiting" | "with" | "open";

export interface MerchantStep {
    key: MerchantStepKey;
    title: string;
    state: MerchantStepState;
    /** The one line under the title on the rail. */
    meta: string;
    /** The access items the step covers. Empty for the questions. */
    items: AccessItem[];
    /** What share-steps names for this step. */
    shareKeys: SetupStepKey[];
    /** The person it was last sent to. Null when it was not sent on. */
    sentTo: string | null;
}

const TITLE: Record<MerchantStepKey, string> = {
    form: "Your business",
    gbp: "Google Business Profile",
    website: "Your website",
    google: "Search Console and Analytics",
};

/** How long each takes, said before they start. Rounded up: a step that runs over its estimate reads as a failure. */
export const MINUTES: Record<MerchantStepKey, number> = { form: 3, gbp: 2, website: 2, google: 1 };

/**
 * Whom a step was last sent to. A send with no `steps` (the old list's
 * "Send the steps") went out with every access step, never the questions.
 */
function sentToFor(m: MerchantOnboarding, keys: SetupStepKey[]): string | null {
    const hit = m.sharedWith.find((p) => (p.steps ? p.steps.some((k) => keys.includes(k)) : !keys.includes("form")));
    return hit ? callName(hit.name) : null;
}

function stateOf(items: AccessItem[], sentTo: string | null): MerchantStepState {
    if (items.length > 0 && items.every(isHeld)) return "done";
    // Theirs is done once every item is held or claimed: we are checking the rest.
    if (items.length > 0 && items.every((a) => isHeld(a) || a.status === "claimed")) return "waiting";
    return sentTo ? "with" : "open";
}

function metaOf(key: MerchantStepKey, state: MerchantStepState, items: AccessItem[], sentTo: string | null): string {
    if (state === "done") return "Done";
    if (state === "waiting") return "Added. We're confirming";
    if (state === "with") return `With ${sentTo}`;
    if (key === "google" && items.length > 1) {
        const held = items.filter(isHeld).length;
        if (held > 0) return `${held} of ${items.length} connected`;
    }
    return `About ${MINUTES[key]} min`;
}

/**
 * The steps, in the order the page asks for them: the questions first, then
 * the two invites (website, then Google Business Profile), then Search
 * Console and Analytics last. A step whose access the record does not ask
 * for (an Online client has no Google Business Profile) is left out, and the
 * questions only show when the record says where they are answered.
 */
export function merchantSteps(m: MerchantOnboarding): MerchantStep[] {
    const steps: MerchantStep[] = [];
    const form = m.form ?? null;
    if (form && (form.inPlatform || form.link || form.answeredAt)) {
        const sentTo = form.answeredAt ? null : sentToFor(m, ["form"]);
        const state: MerchantStepState = form.answeredAt ? "done" : sentTo ? "with" : "open";
        steps.push({ key: "form", title: TITLE.form, state, meta: metaOf("form", state, [], sentTo), items: [], shareKeys: ["form"], sentTo });
    }
    const groups: Array<[MerchantStepKey, AccessItem[]]> = [
        ["website", m.access.filter((a) => a.key === "website")],
        ["gbp", m.access.filter((a) => a.key === "gbp")],
        ["google", m.access.filter((a) => a.key === "gsc" || a.key === "ga4")],
    ];
    for (const [key, items] of groups) {
        if (items.length === 0) continue;
        const shareKeys = items.map((a) => a.key);
        const sentTo = sentToFor(m, shareKeys);
        const state = stateOf(items, sentTo);
        steps.push({ key, title: TITLE[key], state, meta: metaOf(key, state, items, sentTo), items, shareKeys, sentTo });
    }
    return steps;
}

/**
 * The answers the merchant sent, to show back to them. Null when they have
 * not answered, or when what the backend sent cannot be told from Oura's own
 * pre-fill.
 *
 * `answers` is the filed record, whichever way they came in, and wins. An
 * older backend leaves it out; its `draft` is then the answers only when they
 * were sent from this page (it keeps the draft as sent, with `savedAt`),
 * whether or not the page still asks the questions today. A
 * Google Form client never saved here, so their draft is only the pre-fill
 * (the signer, the pre-sale areas): showing it as their answers would put
 * words in their mouth.
 */
export function sentAnswers(form: MerchantForm | null | undefined): SetupAnswers | null {
    if (!form?.answeredAt) return null;
    if (form.answers) return withDefaults(form.answers);
    if (form.savedAt && form.draft) return withDefaults(form.draft);
    return null;
}

/** A step whose merchant part is finished: done, or waiting on us. */
export function isTheirsDone(step: MerchantStep): boolean {
    return step.state === "done" || step.state === "waiting";
}

/** How far through their own part the merchant is, and the minutes left in what is still theirs. */
export function setupTally(steps: MerchantStep[]): { done: number; total: number; minutesLeft: number } {
    const done = steps.filter(isTheirsDone).length;
    const minutesLeft = steps.filter((s) => s.state === "open").reduce((sum, s) => sum + MINUTES[s.key], 0);
    return { done, total: steps.length, minutesLeft };
}

/**
 * The step to show: the first one still theirs after `from`, wrapping round,
 * then one sent to someone else. Null when nothing is left, which is the
 * "All set" view.
 */
export function nextStep(steps: MerchantStep[], from: MerchantStepKey | null = null): MerchantStepKey | null {
    const at = from ? steps.findIndex((s) => s.key === from) : -1;
    const order = [...steps.slice(at + 1), ...steps.slice(0, at + 1)];
    return order.find((s) => s.state === "open")?.key ?? order.find((s) => s.state === "with")?.key ?? null;
}
